/**
 * TasksTab — follow-up tasks for this customer.
 *
 * 2026-10-16 (Bill approved): reads client.retail.followUpTasks.listByCif(cifId)
 * — the server filters dbo.FollowUpTasks to this CIF. It used to read the newest
 * 100 dbo.Tasks rows bank-wide and filter by CIF in the browser, so a
 * customer's tasks silently disappeared once 100 newer tasks existed anywhere.
 * Open tasks list first; a task opened from an application links back to it.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { FollowUpTask } from '@retail-ops/shared-types';
import { Button, DataGrid, Pill } from '@/components/primitives';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { followUpDestination, followUpIsOpen, followUpLabel } from '@/utils/followUpTasks';
import { useApi } from '../hooks/useApi';
import { formatRelative } from '../utils/formatting';

export interface TasksTabProps {
  cifId: string;
}

function statusLabel(s: string): string {
  switch (s) {
    case 'open': return 'Open';
    case 'in-progress': return 'In progress';
    case 'completed': return 'Completed';
    case 'expired': return 'Expired';
    case 'auto-resolved': return 'Resolved';
    default: return s;
  }
}

export function TasksTab({ cifId }: TasksTabProps): JSX.Element {
  const { client, makeSignal } = useApi();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<ReadonlyArray<FollowUpTask> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTasks(null);
    setError(null);
    const signal = makeSignal();
    client.retail.followUpTasks.listByCif(cifId, { signal }).then(
      (res): void => { setTasks(res.tasks ?? []); },
      (err: unknown): void => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Unknown error');
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cifId]);

  const rows = useMemo(
    () => [...(tasks ?? [])].sort((a, b) => {
      const oa = followUpIsOpen(a) ? 0 : 1;
      const ob = followUpIsOpen(b) ? 0 : 1;
      if (oa !== ob) return oa - ob;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }),
    [tasks],
  );

  if (error) return <EmptyState title="Unable to load tasks" message={error} />;
  if (tasks === null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--sp-9)' }}>
        <LoadingSpinner label="Loading tasks…" />
      </div>
    );
  }

  return (
    <DataGrid<FollowUpTask>
      rowKey={(t) => t.id}
      columns={[
        { key: 'task', label: 'Task', render: (t) => (
          <span title={t.notes ?? undefined}>{followUpLabel(t.taskCategory, t.payloadJson)}</span>
        ) },
        { key: 'status', label: 'Status', render: (t) => (
          <Pill variant={followUpIsOpen(t) ? 'warn' : 'ok'}>{statusLabel(t.status)}</Pill>
        ), width: '120px' },
        { key: 'next', label: 'Next action', render: (t) => (t.nextActionAt ? formatRelative(t.nextActionAt) : '—'), width: '120px' },
        { key: 'created', label: 'Created', render: (t) => formatRelative(t.createdAt), width: '110px' },
        { key: 'open', label: '', render: (t) => {
          const dest = followUpDestination(t);
          return dest?.kind === 'cart'
            ? <Button size="sm" variant="ghost" onClick={() => navigate(dest.path)}>Open application</Button>
            : null;
        }, width: '150px' },
      ]}
      rows={rows}
      emptyMessage="No follow-up tasks for this customer."
    />
  );
}
